import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Label,
  Select,
  Spinner,
  TextInput,
  Textarea,
} from "flowbite-react";
import { useForm } from "react-hook-form";
import {
  HiOutlineUser,
  HiPhone,
  HiMail,
  HiLink,
  HiIdentification,
  HiTag,
  HiOutlineLocationMarker,
  HiOutlineCalendar,
} from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { LEADS, USERS, TAGS } from "../../config/apiConfig";
import { useNavigate, useSearchParams } from "react-router-dom";
import AnimatedToast from "../common/AnimatedToast";
/* ============================================================
 * Helpers de máscara e normalização (CPF/CNPJ e telefone BR)
 * ============================================================ */
function onlyDigits(s = "") {
  return String(s).replace(/\D/g, "");
}

function maskCpfCnpj(value = "") {
  const v = onlyDigits(value).slice(0, 14);
  if (v.length <= 11) {
    return v
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return v
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

function maskPhoneBR(value = "") {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length <= 10) {
    return v.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return v.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
}

function isCpfOrCnpj(documentMasked = "") {
  const digits = onlyDigits(documentMasked);
  if (!digits) return null;
  return digits.length > 11 ? "CNPJ" : "CPF";
}

/* ============================================================
 * Componente principal: LeadCreateForm
 * ============================================================ */
export default function LeadCreateForm({
  onCreated,
  onSuccess,
  onSubmit: externalSubmit,
  defaultOwnerId,
  initialData,
  submitLabel = "Criar Lead",
  formId = "lead-create-form",
  hideSubmitButton = false,
  onSubmittingChange,
}) {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [activeTab, setActiveTab] = useState("main");
  const [animatedToast, setAnimatedToast] = useState(null);
  const [customFieldSchema, setCustomFieldSchema] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [loadingCustomFields, setLoadingCustomFields] = useState(true);

  // RHF
  const initialFormValues = useMemo(
    () => ({
      name: initialData?.name || "",
      email: initialData?.email || "",
      phoneNumber: maskPhoneBR(initialData?.phoneNumber || ""),
      companyName: initialData?.companyName || "",
      website: initialData?.website || "",
      document: maskCpfCnpj(initialData?.document || ""),
      source: initialData?.source || "manual",
      leadType: initialData?.leadType || "lead",
      tagId: initialData?.tagId || "",
      notes: initialData?.notes || "",
      consentMarketing: !!initialData?.consentMarketing,
      nextAction: initialData?.nextAction || "",
      nextActionAt: initialData?.nextActionAt || "",
      nextActionNotes: initialData?.nextActionNotes || "",
      ownerId:
        initialData?.ownerId || initialData?.owner?.id || defaultOwnerId || "",
      address: initialData?.address || "",
      addressNeighborhood: initialData?.addressNeighborhood || "",
      addressCity: initialData?.addressCity || "",
      addressState: initialData?.addressState || "",
      addressCountry: initialData?.addressCountry || "",
      addressPostalCode: initialData?.addressPostalCode || "",
    }),
    [initialData, defaultOwnerId]
  );

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: initialFormValues,
  });

  useEffect(() => {
    reset(initialFormValues);
  }, [initialFormValues, reset]);

  const documentValue = watch("document");
  const documentType = useMemo(
    () => isCpfOrCnpj(documentValue),
    [documentValue]
  );

  // Pré-carrega opções de responsável e tags (pode adaptar para seus endpoints)
  const selectedOwnerId = useMemo(() => {
    const candidate =
      initialData?.ownerId || initialData?.owner?.id || defaultOwnerId;
    return candidate != null ? Number(candidate) : null;
  }, [initialData, defaultOwnerId]);

  useEffect(() => {
    (async () => {
      try {
        const [usersRes, tagsRes] = await Promise.all([
          axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`),
          axios
            .get(`${TAGS}?itemsPerPage=-1`)
            .catch(() => ({ data: { data: { data: [] } } })),
        ]);
        const owners = (usersRes?.data?.data?.data || []).map((u) => ({
          value: u.id,
          label: u.name || u.email,
        }));
        setOwnerOptions(owners);
        const tags = (tagsRes?.data?.data?.data || []).map((t) => ({
          value: t.id,
          label: t.name,
        }));
        setTagOptions(tags);
      } catch (e) {
        console.error("LeadCreateForm: load options", e);
      }
    })();
  }, [USERS, TAGS, selectedOwnerId]);

  useEffect(() => {
    let active = true;
    const loadCustomFields = async () => {
      setLoadingCustomFields(true);
      try {
        const { data } = await axios.get(LEADS.CUSTOM_FIELDS);
        if (active) {
          setCustomFieldSchema(Array.isArray(data?.data) ? data.data : []);
        }
      } catch (error) {
        console.error("LeadCreateForm: carregar campos customizados", error);
        setAnimatedToast({
          id: Date.now(),
          title: "Erro",
          message:
            error?.response?.data?.message ||
            "Não foi possível carregar os campos customizados.",
        });
      } finally {
        if (active) {
          setLoadingCustomFields(false);
        }
      }
    };
    loadCustomFields();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(initialData?.customFieldValues)) return;
    const mapped = {};
    initialData.customFieldValues.forEach((item) => {
      if (item?.customFieldId) {
        mapped[item.customFieldId] = item.value;
      }
    });
    setCustomFieldValues(mapped);
  }, [initialData?.customFieldValues]);

  useEffect(() => {
    if (selectedOwnerId != null && ownerOptions.length) {
      const found = ownerOptions.find(
        (option) => Number(option.value) === selectedOwnerId
      );
      if (found) {
        setValue("ownerId", found.value);
      }
    }
  }, [ownerOptions, selectedOwnerId, setValue]);

  // UTM da URL (utm_source, utm_medium, utm_campaign, utm_term, utm_content)
  const utm = useMemo(() => {
    const keys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
    ];
    const obj = {};
    keys.forEach((k) => {
      const v = searchParams.get(k);
      if (v) obj[k] = v;
    });
    return Object.keys(obj).length ? obj : null;
  }, [searchParams]);

  // Máscaras controladas
  const handleMaskDoc = (e) => {
    setValue("document", maskCpfCnpj(e.target.value));
  };
  const handleMaskPhone = (e) => {
    setValue("phoneNumber", maskPhoneBR(e.target.value));
  };

  const renderMainInfoFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name" value="Nome do contato (*)" />
          <TextInput
            id="name"
            icon={HiOutlineUser}
            placeholder="Ex.: Maria Silva"
            required
            {...register("name", { required: true })}
          />
          {errors.name && (
            <small className="text-red-600">Informe o nome.</small>
          )}
        </div>
        <div>
          <Label htmlFor="phoneNumber" value="Telefone (*)" />
          <TextInput
            id="phoneNumber"
            icon={HiPhone}
            placeholder="(11) 91234-5678"
            value={watch("phoneNumber")}
            onChange={handleMaskPhone}
            required
          />
        </div>

        <div>
          <Label htmlFor="email" value="E-mail" />
          <TextInput
            id="email"
            type="email"
            icon={HiMail}
            placeholder="contato@empresa.com"
            {...register("email")}
          />
        </div>

        <div>
          <Label htmlFor="document" value="Documento (CPF/CNPJ)" />
          <TextInput
            id="document"
            icon={HiIdentification}
            value={watch("document")}
            onChange={handleMaskDoc}
          />
          {documentType && (
            <small className="text-xs text-gray-500">{documentType}</small>
          )}
        </div>
        <div>
          <Label htmlFor="leadType" value="Tipo" />
          <Select id="leadType" {...register("leadType")}>
            <option value="lead">Lead</option>
            <option value="client">Cliente</option>
          </Select>
        </div>

        <div>
          <Label htmlFor="ownerId" value="Responsável" />
          <Select id="ownerId" {...register("ownerId")}>
            <option value="">Selecione o responsável</option>
            {ownerOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
      </div>
      {renderCustomFieldsSection()}
    </>
  );

  const renderCompanyInfoFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="companyName" value="Empresa" />
        <TextInput
          id="companyName"
          placeholder="Razão social / Nome fantasia"
          {...register("companyName")}
        />
      </div>

      <div>
        <Label htmlFor="website" value="Website" />
        <TextInput
          id="website"
          icon={HiLink}
          placeholder="https://..."
          {...register("website")}
        />
      </div>

      <div>
        <Label htmlFor="source" value="Origem" />
        <Select id="source" {...register("source")}>
          <option value="manual">Manual</option>
          <option value="website">Website</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
          <option value="google_ads">Google Ads</option>
          <option value="organic">Orgânico</option>
          <option value="referral">Indicação</option>
          <option value="event">Evento</option>
          <option value="import">Importação</option>
        </Select>
      </div>

      <div>
        <Label htmlFor="tagId" value="Tag" />
        <Select id="tagId" {...register("tagId")}>
          <option value="">Nenhuma tag</option>
          {tagOptions.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );

  const renderAddressFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <div>
        <Label htmlFor="addressPostalCode" value="CEP" />
        <TextInput
          id="addressPostalCode"
          placeholder="00000-000"
          {...register("addressPostalCode")}
        />
      </div>
      <div>
        <Label htmlFor="address" value="Endereço" />
        <TextInput
          id="address"
          placeholder="Logradouro, número, complemento"
          {...register("address")}
        />
      </div>

      <div>
        <Label htmlFor="addressNeighborhood" value="Bairro" />
        <TextInput
          id="addressNeighborhood"
          placeholder="Bairro"
          {...register("addressNeighborhood")}
        />
      </div>

      <div>
        <Label htmlFor="addressCity" value="Cidade" />
        <TextInput
          id="addressCity"
          placeholder="Cidade"
          {...register("addressCity")}
        />
      </div>

      <div>
        <Label htmlFor="addressState" value="Estado" />
        <TextInput
          id="addressState"
          placeholder="Estado (UF)"
          maxLength={2}
          {...register("addressState")}
        />
      </div>

      <div>
        <Label htmlFor="addressCountry" value="País" />
        <TextInput
          id="addressCountry"
          placeholder="País"
          {...register("addressCountry")}
        />
      </div>
    </div>
  );

  const handleCustomFieldChange = (fieldId, nextValue) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [fieldId]: nextValue,
    }));
  };

  const formatCustomFieldValueForPayload = (field, value) => {
    let normalized = value;
    if (["checkbox", "boolean"].includes(field.type)) {
      normalized =
        normalized === true ||
        normalized === "true" ||
        normalized === "1" ||
        normalized === 1
          ? "true"
          : "false";
    }
    if (normalized === undefined || normalized === null) {
      normalized = "";
    }
    return normalized;
  };

  const renderCustomFieldInput = (field) => {
    const value = customFieldValues[field.id] ?? "";
    const baseProps = {
      id: `custom-field-${field.id}`,
      value,
      onChange: (event) =>
        handleCustomFieldChange(field.id, event.target.value),
      placeholder: field.placeholder || "",
    };

    switch (field.type) {
      case "textarea":
        return <Textarea {...baseProps} rows={3} />;
      case "number":
      case "currency":
      case "percent":
        return (
          <TextInput
            {...baseProps}
            inputMode="decimal"
            type="text"
            pattern="^[0-9.,]*$"
          />
        );
      case "date":
        return <TextInput {...baseProps} type="date" />;
      case "datetime":
        return <TextInput {...baseProps} type="datetime-local" />;
      case "select":
        return (
          <Select
            {...baseProps}
            value={value}
            onChange={(event) =>
              handleCustomFieldChange(field.id, event.target.value)
            }
          >
            <option value="">Selecione</option>
            {(field.options || []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        );
      case "radio":
        return (
          <div className="flex flex-wrap gap-3">
            {(field.options || []).map((option) => (
              <label
                key={option}
                className="inline-flex items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="radio"
                  name={`radio-${field.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(event) =>
                    handleCustomFieldChange(field.id, event.target.value)
                  }
                />
                {option}
              </label>
            ))}
          </div>
        );
      case "checkbox":
      case "boolean":
        return (
          <div className="flex items-center gap-3">
            <input
              id={`custom-field-${field.id}`}
              type="checkbox"
              checked={
                value === "true" ||
                value === true ||
                value === "1" ||
                value === 1
              }
              onChange={(event) =>
                handleCustomFieldChange(
                  field.id,
                  event.target.checked ? "true" : ""
                )
              }
            />
            <Label htmlFor={`custom-field-${field.id}`}>
              {field.placeholder || "Selecionado"}
            </Label>
          </div>
        );
      default:
        return (
          <TextInput
            {...baseProps}
            type="text"
            inputMode="text"
            autoComplete="off"
          />
        );
    }
  };

  const renderCustomFieldsSection = () => {
    if (!customFieldSchema.length) return null;
    return (
      <div className="space-y-3 pt-3 mt-4">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">
            Campos adicionais
          </p>
          {loadingCustomFields && <Spinner size="sm" />}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {customFieldSchema.map((field) => (
            <div key={field.id} className=" space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-900">
                  {field.label}
                </p>
                {field.isRequired && (
                  <span className="text-xs text-rose-600">(*)</span>
                )}
              </div>
              {renderCustomFieldInput(field)}
              {(field.type === "select" || field.type === "radio") &&
                !field.options?.length && (
                  <p className="text-[11px] text-amber-600">
                    Adicione opções primeiro para este campo.
                  </p>
                )}
            </div>
          ))}
        </div>
      </div>
    );
  };
  const renderNotesFields = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="notes" value="Notas" />
        <Textarea
          id="notes"
          rows={4}
          placeholder="Observações gerais, contexto do lead, dores..."
          {...register("notes")}
        />
      </div>

      <div className="flex items-center gap-3">
        <Checkbox id="consentMarketing" {...register("consentMarketing")} />
        <Label htmlFor="consentMarketing">
          Tenho consentimento do lead para comunicações de marketing
        </Label>
      </div>
    </div>
  );

  const tabSections = [
    {
      id: "main",
      label: "Informações principais",
      description: "Nome, contato e documento do lead",
      icon: HiOutlineUser,
      render: renderMainInfoFields,
    },
    {
      id: "company",
      label: "Empresa & origem",
      description: "Responsável, tag e canal de conversão",
      icon: HiTag,
      render: renderCompanyInfoFields,
    },
    {
      id: "address",
      label: "Endereço",
      description: "Localização completa do lead ou empresa",
      icon: HiOutlineLocationMarker,
      render: renderAddressFields,
    },
    {
      id: "notes",
      label: "Observações",
      description: "Notas internas e consentimentos de marketing",
      icon: HiOutlineCalendar,
      render: renderNotesFields,
    },
  ];

  const currentTab =
    tabSections.find((tab) => tab.id === activeTab) || tabSections[0];

  // Submissão
  const defaultSubmit = async (payload) => {
    if (initialData?.id) {
      const { data } = await axios.put(LEADS.UPDATE(initialData.id), payload);
      return data?.data || data;
    }
    const { data } = await axios.post(`${LEADS.CREATE}`, payload);
    return data?.data || data;
  };

  const showSuccessToast = () => {
    const normalizedLabel = submitLabel.toLowerCase();
    const message = normalizedLabel.includes("criar")
      ? "Lead criado com sucesso."
      : "Lead salvo com sucesso.";
    setAnimatedToast({
      id: Date.now(),
      message,
      title: "Sucesso",
      duration: 4000,
    });
  };

  const handleSuccess = (result) => {
    showSuccessToast();
    if (onSuccess) {
      onSuccess(result);
      return;
    }
    if (onCreated) onCreated(result);
  };

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      // Monta payload normalizado (server também normaliza no hook)
      const missingCustomField = customFieldSchema.find((field) => {
        if (!field.isRequired) return false;
        const raw = customFieldValues[field.id];
        if (["checkbox", "boolean"].includes(field.type)) {
          return !(raw === true || raw === "true" || raw === "1" || raw === 1);
        }
        return !String(raw ?? "").trim();
      });
      if (missingCustomField) {
        setAnimatedToast({
          id: Date.now(),
          title: "Erro",
          message: `Preencha o campo "${missingCustomField.label}".`,
        });
        setIsSubmitting(false);
        return;
      }

      const customFieldPayload = customFieldSchema
        .map((field) => {
          const rawValue = customFieldValues[field.id];
          const normalized = formatCustomFieldValueForPayload(field, rawValue);
          const shouldSend = ["checkbox", "boolean"].includes(field.type)
            ? normalized === "true"
            : String(normalized).trim().length > 0;
          if (!shouldSend) return null;
          return {
            customFieldId: field.id,
            value: normalized,
          };
        })
        .filter(Boolean);

      const payload = {
        name: values.name?.trim(),
        email: values.email?.trim() || null,
        phoneNumber: values.phoneNumber || null,
        companyName: values.companyName?.trim() || null,
        website: values.website?.trim() || null,
        document: values.document || null, // masked; backend normaliza para dígitos
        source: values.source || "manual",
        tagId: values.tagId || null,
        notes: values.notes || null,
        consentMarketing: !!values.consentMarketing,
        nextAction: values.nextAction || null,
        nextActionAt: values.nextActionAt || null, // string ISO local -> backend em UTC
        nextActionNotes: values.nextActionNotes || null,
        ownerId: values.ownerId || null,
        leadType: values.leadType || "lead",
        utm,
        address: values.address?.trim() || null,
        addressNeighborhood: values.addressNeighborhood?.trim() || null,
        addressCity: values.addressCity?.trim() || null,
        addressState: values.addressState?.trim() || null,
        addressCountry: values.addressCountry?.trim() || null,
        addressPostalCode: values.addressPostalCode?.trim() || null,
        customFieldValues: customFieldPayload,
      };

      const action = externalSubmit || defaultSubmit;
      const result = await action(payload);
      handleSuccess(result);
    } catch (error) {
      console.error("create lead", error);
      const msg =
        error?.response?.data?.message ||
        error.message ||
        "Erro ao criar lead.";
      setAnimatedToast({
        id: Date.now(),
        message: msg,
        title: "Erro",
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (typeof onSubmittingChange === "function") {
      onSubmittingChange(isSubmitting);
    }
  }, [isSubmitting, onSubmittingChange]);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-5">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex flex-wrap gap-2">
              {tabSections.map((tab) => {
                const isActive = tab.id === activeTab;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`rounded-full border px-3 py-2 text-sm font-semibold transition ${
                      isActive
                        ? "border-[#e45412] bg-orange-50 text-primary shadow-sm"
                        : "border-transparent bg-white/70 text-gray-600 hover:border-gray-200"
                    }`}
                    aria-pressed={isActive}
                  >
                    <span className="flex items-center gap-2">
                      <tab.icon className="text-lg" aria-hidden />
                      <span className="hidden md:inline">{tab.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500 block md:hidden">
              {currentTab.label}
            </p>
          </div>
          <div className="p-4">{currentTab.render()}</div>
        </div>
      </div>

      {/* Rodapé */}
      {!hideSubmitButton && (
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting} className="bg-primary">
            <div className="flex gap-1 items-center">
              {isSubmitting ? <Spinner size="sm" /> : null}
              <span>{submitLabel}</span>
            </div>
          </Button>
        </div>
      )}
      <AnimatedToast
        open={Boolean(animatedToast)}
        message={animatedToast?.message}
        title={animatedToast?.title}
        duration={animatedToast?.duration}
        onClose={() => setAnimatedToast(null)}
      />
    </form>
  );
}
